import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, Check } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { applyPartner } from '../lib/marketingApi';
import { isValidPhone } from '../lib/phone';
import { PARTNER_INTEREST_OPTIONS, PARTNER_ROLE_OPTIONS, EMPLOYEE_COUNT_OPTIONS } from '../data/content';
import { Btn } from './ui';
import CaptchaWidget from './CaptchaWidget';
import { cn } from '@/lib/utils';

const inputBase =
  'w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-colors focus:border-brand focus:ring-2 focus:ring-brand/20 dark:border-white/15 dark:bg-white/[0.04] dark:text-white dark:placeholder:text-slate-500';
const labelBase = 'mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200';

function getUtm() {
  if (typeof window === 'undefined') return {};
  const p = new URLSearchParams(window.location.search);
  return {
    utm_source: p.get('utm_source') || undefined,
    utm_medium: p.get('utm_medium') || undefined,
    utm_campaign: p.get('utm_campaign') || undefined,
  };
}

export default function PartnerForm({ className }) {
  const { lang } = useLanguage();
  const [form, setForm] = useState({
    name: '', email: '', company: '', phone: '', website: '',
    employee_count: '', role: '', message: '',
  });
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [capEnabled, setCapEnabled] = useState(false);
  const [hp, setHp] = useState('');
  const capRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleInterest = (v) =>
    setInterests((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Please add your name and work email.');
      return;
    }
    if (!isValidPhone(form.phone)) {
      toast.error('Please enter a valid US, UK or Australian phone number.');
      return;
    }
    if (interests.length === 0) {
      toast.error('Please select at least one area of interest.');
      return;
    }
    let capToken = '';
    if (capEnabled) {
      capToken = await capRef.current?.getToken();
      if (!capToken) {
        toast.error('Anti-spam check failed. Please try again.');
        return;
      }
    }
    setLoading(true);
    try {
      await applyPartner({
        ...form,
        interests,
        source: 'partners_page',
        language: lang,
        page: typeof window !== 'undefined' ? window.location.pathname : undefined,
        captcha_token: capToken,
        hp,
        ...getUtm(),
      });
      setDone(true);
      toast.success('Application received — thank you!');
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
      capRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="partner-form-success"
        className={cn('flex flex-col items-center gap-4 rounded-2xl border border-slate-200 bg-white p-8 text-center dark:border-white/10 dark:bg-white/[0.03]', className)}
      >
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-brand/10 text-brand">
          <CheckCircle2 className="h-7 w-7" />
        </span>
        <h3 className="font-display text-xl font-bold text-slate-900 dark:text-white">Application received</h3>
        <p className="max-w-sm text-sm text-slate-600 dark:text-slate-300">
          Thanks for your interest in partnering with BillPortal. Our partnerships team will review your application and reach out within one business day.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      data-testid="partner-application-form"
      className={cn('rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-white/[0.03] sm:p-7', className)}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className={labelBase} htmlFor="pf-name">Full name *</label>
          <input id="pf-name" data-testid="partner-name" className={inputBase} value={form.name} onChange={set('name')} placeholder="Jane Doe" required />
        </div>
        <div>
          <label className={labelBase} htmlFor="pf-email">Work email *</label>
          <input id="pf-email" data-testid="partner-email" type="email" className={inputBase} value={form.email} onChange={set('email')} placeholder="jane@company.com" required />
        </div>
        <div>
          <label className={labelBase} htmlFor="pf-company">Company</label>
          <input id="pf-company" data-testid="partner-company" className={inputBase} value={form.company} onChange={set('company')} placeholder="Company name" />
        </div>
        <div>
          <label className={labelBase} htmlFor="pf-website">Website</label>
          <input id="pf-website" data-testid="partner-website" className={inputBase} value={form.website} onChange={set('website')} placeholder="https://company.com" />
        </div>
        <div>
          <label className={labelBase} htmlFor="pf-phone">Phone *</label>
          <input id="pf-phone" data-testid="partner-phone" type="tel" className={inputBase} value={form.phone} onChange={set('phone')} placeholder="+1, +44 or +61 …" required />
          <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">US, UK or Australian number — include your country code.</p>
        </div>
        <div>
          <label className={labelBase} htmlFor="pf-employees">Number of employees</label>
          <select id="pf-employees" data-testid="partner-employees" className={inputBase} value={form.employee_count} onChange={set('employee_count')}>
            <option value="">Select a range…</option>
            {EMPLOYEE_COUNT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className={labelBase} htmlFor="pf-role">Your role</label>
          <select id="pf-role" data-testid="partner-role" className={inputBase} value={form.role} onChange={set('role')}>
            <option value="">Select your role…</option>
            {PARTNER_ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-5">
        <label className={labelBase}>What are you interested in? *</label>
        <p className="mb-2.5 text-xs text-slate-400 dark:text-slate-500">Select all that apply.</p>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="partner-interests">
          {PARTNER_INTEREST_OPTIONS.map((o) => {
            const active = interests.includes(o.value);
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => toggleInterest(o.value)}
                data-testid={`partner-interest-${o.value}`}
                aria-pressed={active}
                className={cn(
                  'flex items-center gap-2.5 rounded-xl border px-4 py-2.5 text-left text-sm font-medium transition-all',
                  active
                    ? 'border-brand bg-brand/5 text-slate-900 dark:text-white'
                    : 'border-slate-300 text-slate-600 hover:border-brand/40 dark:border-white/15 dark:text-slate-300'
                )}
              >
                <span className={cn('flex h-4 w-4 shrink-0 items-center justify-center rounded border', active ? 'border-brand bg-brand text-white' : 'border-slate-300 dark:border-white/25')}>
                  {active && <Check className="h-3 w-3" />}
                </span>
                {o.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-5">
        <label className={labelBase} htmlFor="pf-message">Tell us about your business</label>
        <textarea id="pf-message" data-testid="partner-message" rows={4} className={inputBase} value={form.message} onChange={set('message')} placeholder="Who are your clients and how would you like to work with BillPortal?" />
      </div>

      <div className="mt-6 flex flex-col gap-3">
        {/* honeypot — hidden from humans */}
        <input value={hp} onChange={(e) => setHp(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 opacity-0" />
        <CaptchaWidget ref={capRef} onEnabledChange={setCapEnabled} className="mt-1" />
        <Btn type="submit" size="lg" full disabled={loading} data-testid="partner-submit-btn">
          {loading ? (<><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</>) : 'Apply to partner'}
        </Btn>
        <p className="text-center text-xs leading-relaxed text-slate-400 dark:text-slate-500">
          By applying you agree to our{' '}
          <Link to="/legal/partner-terms" className="font-medium text-brand hover:underline">Partner Terms &amp; Conditions</Link>.
        </p>
      </div>
    </form>
  );
}
